export const APP_BACK_PROTOCOL = 'hardware-back-v1';

export const APP_BACK_ROUTE_POLICY = Object.freeze({
  ROOT: 'ROOT',
  HOME: 'HOME',
  HISTORY: 'HISTORY',
  PARENT: 'PARENT',
  UNSUPPORTED: 'UNSUPPORTED',
});

const ROOT_PATHS = new Set(['/', '/main']);
const HOME_PATHS = new Set(['/saju-input', '/saju-service', '/selection', '/fortune']);
const AUDITED_PATHS = new Set([
  ...ROOT_PATHS,
  ...HOME_PATHS,
  '/about-nangman',
  '/mypage/history',
  '/mypage/inquiry',
  '/mypage/inquiryFinish',
  '/mypage/inquiryGuest',
  '/mypage/inquiryList',
  '/mypage/moreinfo',
  '/mypage/myinfo',
  '/mypage/subscription',
  '/nangman-center',
  '/purchase/exchange',
  '/purchase/gift-box',
  '/purchase/gift-claim',
  '/purchase/points',
  '/purchase/tickets',
]);
const AUDITED_PATH_PATTERNS = [
  /^\/analysis(?:\/.*)?$/,
  /^\/m\/saju-insight(?:\/.*)?$/,
  /^\/mobile\/(?:analysis-saju|five-element|saju-)(?:.*)?$/,
  /^\/mypage\/inquiryView(?:\/(?:inquiry|eventinquiry)\/[^/]+)?$/,
  /^\/mypage\/subscrPaymentView\/[^/]+$/,
  /^\/nangman-center\/[^/]+(?:\/[^/]+)?$/,
  /^\/purchase\/ticket-detail(?:\/.*)?$/,
  /^\/saju-result(?:\/.*)?$/,
];

const stripTrailingSlash = (path) => {
  if (!path || path === '/') return path || '/';
  return path.replace(/\/+$/, '');
};

export const normalizeAppBackPath = (path) => stripTrailingSlash(path?.split(/[?#]/, 1)[0] || '/');

export const isAppBackRouteSupported = (rawPath) => {
  const path = normalizeAppBackPath(rawPath);
  return AUDITED_PATHS.has(path) || AUDITED_PATH_PATTERNS.some((pattern) => pattern.test(path));
};

export const resolveAppBackParent = (rawPath) => {
  const path = normalizeAppBackPath(rawPath);

  if (path === '/about-nangman') return '/main';

  const boardDetail = path.match(/^\/nangman-center\/([^/]+)\/[^/]+$/);
  if (boardDetail) return `/nangman-center/${boardDetail[1]}`;

  if (/^\/mypage\/inquiryView\/(?:inquiry|eventinquiry)\/[^/]+$/.test(path)) {
    return '/mypage/inquiry';
  }

  if (path === '/mypage/inquiryGuest' || path === '/mypage/inquiryFinish') {
    return '/mypage/inquiry';
  }

  if (/^\/mypage\/subscrPaymentView\/[^/]+$/.test(path)) {
    return '/mypage/history';
  }

  if (path.startsWith('/purchase/ticket-detail/')) return '/purchase/tickets';
  if (path === '/purchase/gift-claim') return '/purchase/gift-box';

  if (
    path === '/saju-result' ||
    path.startsWith('/saju-result/') ||
    path === '/analysis' ||
    path.startsWith('/analysis/') ||
    path.startsWith('/mobile/') ||
    path === '/m/saju-insight' ||
    path.startsWith('/m/saju-insight/')
  ) {
    return '/saju-service';
  }

  if (
    path === '/purchase' ||
    path.startsWith('/purchase/') ||
    path === '/mypage' ||
    path.startsWith('/mypage/') ||
    path === '/nangman-center' ||
    path.startsWith('/nangman-center/')
  ) {
    return '/main';
  }

  return null;
};

export const isInternalHistoryBack = (back, { origin, base = '/' } = {}) => {
  if (typeof back !== 'string' || !back) return false;

  try {
    const resolvedOrigin = origin || window.location.origin;
    const url = new URL(back, resolvedOrigin);
    const normalizedBase = base === '/' ? '/' : `/${base.replace(/^\/+|\/+$/g, '')}`;
    const isInsideBase =
      normalizedBase === '/' ||
      url.pathname === normalizedBase ||
      url.pathname.startsWith(`${normalizedBase}/`);

    return url.origin === resolvedOrigin && isInsideBase;
  } catch {
    return false;
  }
};

export const isOwnedAppBackHistory = (
  historyState,
  watermark,
  { origin, base = '/' } = {},
) => {
  if (!Number.isInteger(historyState?.position) || !Number.isInteger(watermark)) return false;
  if (historyState.position <= watermark) return false;

  return isInternalHistoryBack(historyState.back, { origin, base });
};

export const resolveAppBackRoute = (
  rawPath,
  { historyState, historyWatermark = 0, origin, base = '/' } = {},
) => {
  const path = normalizeAppBackPath(rawPath);

  if (ROOT_PATHS.has(path)) {
    return { policy: APP_BACK_ROUTE_POLICY.ROOT, target: null };
  }

  if (HOME_PATHS.has(path)) {
    return { policy: APP_BACK_ROUTE_POLICY.HOME, target: '/main' };
  }

  if (!isAppBackRouteSupported(path)) {
    return { policy: APP_BACK_ROUTE_POLICY.UNSUPPORTED, target: null };
  }

  if (
    isOwnedAppBackHistory(historyState, historyWatermark, {
      origin,
      base,
    })
  ) {
    return { policy: APP_BACK_ROUTE_POLICY.HISTORY, target: null };
  }

  const parent = resolveAppBackParent(path);
  if (parent && parent !== '/main') {
    return { policy: APP_BACK_ROUTE_POLICY.PARENT, target: parent };
  }

  return { policy: APP_BACK_ROUTE_POLICY.HOME, target: '/main' };
};
